import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapPin, Pencil } from 'lucide-react';
import { locationHubService } from '../../services/locationHubService';
import Alert from '../common/Alert';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
} from '../common/index.js';

const ViewLocationHub = () => {
  const { hubId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hubData, setHubData] = useState(null);

  useEffect(() => {
    fetchHub();
  }, [hubId]);

  const fetchHub = async () => {
    try {
      setLoading(true);
      const response = await locationHubService.getHub(hubId);
      const hub = response.data.hub || response.data.data;

      if (hub) {
        setHubData(hub);
      } else {
        Alert.error('Destination hub not found');
      }
    } catch (error) {
      console.error('Error fetching hub:', error);
      Alert.error(error.response?.data?.error || 'Failed to load destination hub');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <PageLayout title="Destination Hub" icon={MapPin}>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading destination hub...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!hubData) {
    return (
      <PageLayout title="Destination Hub" icon={MapPin}>
        <DetailCard>
          <p className="text-center text-red-600 font-medium mb-4">Destination Hub Not Found</p>
          <div className="flex justify-center">
            <Button onClick={() => navigate('/location-hubs')}>Back to Hubs</Button>
          </div>
        </DetailCard>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Destination Hub Details"
      icon={MapPin}
      actions={
        <>
          <Button variant="outline" onClick={() => navigate('/location-hubs')}>
            Back to Hubs
          </Button>
          <Button icon={Pencil} onClick={() => navigate(`/location-hubs/edit/${hubId}`)}>
            Edit Hub
          </Button>
        </>
      }
    >
      <DetailCard title="Hub Information">
        <DetailRow label="Hub ID" value={hubData.hub_id} />
        <DetailRow label="Destination Code" value={hubData.location_code} />
        <DetailRow label="Destination" value={hubData.location} />
        <DetailRow label="State" value={hubData.state} />
        <DetailRow
          label="Destination Type"
          value={
            hubData.destination_type === 'middle_destination'
              ? 'Middle Destination'
              : 'Branch Destination'
          }
        />
        <DetailRow
          label="Commission (%)"
          value={
            hubData.commission_percentage !== undefined && hubData.commission_percentage !== null
              ? `${hubData.commission_percentage}%`
              : '0%'
          }
        />
        <DetailRow label="Address" value={hubData.address} />
        <DetailRow label="Contact" value={hubData.location_hub_contact || 'N/A'} />
        <DetailRow label="Created By" value={hubData.created_by || 'N/A'} />
        <DetailRow label="Created Date" value={formatDateTime(hubData.created_at)} />
      </DetailCard>
    </PageLayout>
  );
};

export default ViewLocationHub;
